import { useCallback, useMemo, useState } from "react";
import { FileText, Upload, X } from "lucide-react";
import { AnimatedDialog, AlertMessage, Button } from "@/components/reusable";
import {
  LockedProviderField,
  ProviderSelector,
  type ProviderSelectionMeta,
} from "@/components/providers/ProviderSelector";
import { MultiFileUploadField } from "@/components/upload/MultiFileUploadField";
import { PendingPipelineFilesList } from "@/components/upload/pipeline/PendingPipelineFilesList";
import { schemaRequiresReportType } from "@/config/reportType";
import { useUploadSchemas } from "@/hooks/useUploadSchemas";
import { useNavigateKeepParams } from "@/hooks/useNavigateKeepParams";
import { useTelemetry } from "@/hooks/useTelemetry";
import { canSubmitPendingPipelineUploads, type PendingPipelineUpload } from "@/lib/pipelineTypes";
import { setPipelineUploadHandoff } from "@/lib/pipelineUploadHandoff";
import { classifyFile } from "@/services/api";
import * as Dialog from "@radix-ui/react-dialog";

const HOME_UPLOAD_MAX_FILES = 50;

function pendingFileKey(file: File): string {
  return `${file.name}::${file.size}::${file.lastModified}`;
}

export function HomePage() {
  const navigate = useNavigateKeepParams();
  const [providerId, setProviderId] = useState<string | null>(null);
  const [providerName, setProviderName] = useState<string | null>(null);
  const {
    schemas,
    isLoadingSchemas,
    error: schemasError,
    setError: setSchemasError,
  } = useUploadSchemas(providerId);
  const { track, trackError } = useTelemetry();

  const [pendingEntries, setPendingEntries] = useState<PendingPipelineUpload[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [defaultSchemaId, setDefaultSchemaId] = useState("");

  const schemaNameById = useMemo(
    () => Object.fromEntries(schemas.map((schema) => [schema.id, schema.name])),
    [schemas]
  );

  const providerRequired = true;
  const providerReady = providerId !== null;

  const handleProviderChange = useCallback(
    (nextProviderId: string | null, meta?: ProviderSelectionMeta) => {
      setProviderId(nextProviderId);
      setProviderName(nextProviderId ? (meta?.name ?? null) : null);
      setDefaultSchemaId("");
      setPendingEntries((current) =>
        current.map((entry) => ({
          file: entry.file,
          schemaId: "",
        }))
      );
      track("provider_selected", { providerId: nextProviderId ?? "none" });
    },
    [track]
  );

  const classifyPendingEntry = useCallback(
    async (fileKey: string, file: File, schemaId: string) => {
      try {
        const result = await classifyFile(file, schemaId);
        setPendingEntries((current) =>
          current.map((entry) =>
            pendingFileKey(entry.file) === fileKey
              ? {
                  ...entry,
                  reportType: result.detectedType ?? entry.reportType ?? null,
                  allowedReportTypes: result.allowedReportTypes ?? [],
                  detectedType: result.detectedType ?? null,
                  isClassifying: false,
                }
              : entry
          )
        );
      } catch (err) {
        trackError(err, { context: "home_classify" });
        setPendingEntries((current) =>
          current.map((entry) =>
            pendingFileKey(entry.file) === fileKey
              ? { ...entry, isClassifying: false, allowedReportTypes: [], detectedType: null }
              : entry
          )
        );
      }
    },
    [trackError]
  );

  const handlePendingFilesAdd = useCallback(
    (nextFiles: File[]) => {
      if (!providerReady) {
        setUploadError("Select a provider first.");
        return;
      }
      const previousKeys = new Set(pendingEntries.map((entry) => pendingFileKey(entry.file)));
      const schemaId = defaultSchemaId || schemas[0]?.id || "";
      const nextEntries = nextFiles.map((file) => {
        const existing = pendingEntries.find(
          (entry) => pendingFileKey(entry.file) === pendingFileKey(file)
        );
        return existing ?? { file, schemaId };
      });
      setPendingEntries(nextEntries);
      // Do not clear uploadError here — MultiFileUploadField owns limit/validation
      // messages via onError and must not be overwritten after truncate.

      for (const entry of nextEntries) {
        const fileKey = pendingFileKey(entry.file);
        if (previousKeys.has(fileKey) || !entry.schemaId) continue;
        const schemaName = schemaNameById[entry.schemaId] ?? entry.schemaId;
        if (schemaRequiresReportType(schemaName)) {
          setPendingEntries((current) =>
            current.map((item) =>
              pendingFileKey(item.file) === fileKey ? { ...item, isClassifying: true } : item
            )
          );
          void classifyPendingEntry(fileKey, entry.file, entry.schemaId);
        }
      }

      if (nextFiles.length > 0) {
        track("file_selected", {
          fileCount: nextFiles.length,
          fileExtension: nextFiles[0]?.name.split(".").pop()?.toLowerCase() ?? "unknown",
          fileSizeKB: Math.round(nextFiles.reduce((sum, f) => sum + f.size, 0) / 1024),
          batchMode: nextFiles.length > 1,
          source: "home",
        });
      }
    },
    [
      classifyPendingEntry,
      defaultSchemaId,
      pendingEntries,
      providerReady,
      schemaNameById,
      schemas,
      track,
    ]
  );

  const handlePendingEntrySchemaChange = useCallback(
    (index: number, schemaId: string) => {
      setDefaultSchemaId(schemaId);
      const entry = pendingEntries[index];
      if (!entry) return;
      const fileKey = pendingFileKey(entry.file);
      const schemaName = schemaNameById[schemaId] ?? schemaId;
      const needsReportType = schemaRequiresReportType(schemaName);

      setPendingEntries((current) =>
        current.map((item, entryIndex) =>
          entryIndex === index
            ? {
                file: item.file,
                schemaId,
                reportType: needsReportType ? null : undefined,
                allowedReportTypes: needsReportType ? [] : undefined,
                detectedType: needsReportType ? null : undefined,
                isClassifying: needsReportType,
              }
            : item
        )
      );

      if (needsReportType) {
        void classifyPendingEntry(fileKey, entry.file, schemaId);
      }
    },
    [classifyPendingEntry, pendingEntries, schemaNameById]
  );

  const handlePendingAllSchemasChange = useCallback(
    (schemaId: string) => {
      if (schemaId) setDefaultSchemaId(schemaId);
      const schemaName = schemaNameById[schemaId] ?? schemaId;
      const needsReportType = !!schemaId && schemaRequiresReportType(schemaName);

      setPendingEntries((current) =>
        current.map((item) => ({
          file: item.file,
          schemaId,
          reportType: needsReportType ? null : undefined,
          allowedReportTypes: needsReportType ? [] : undefined,
          detectedType: needsReportType ? null : undefined,
          isClassifying: needsReportType,
        }))
      );

      if (needsReportType) {
        for (const entry of pendingEntries) {
          void classifyPendingEntry(pendingFileKey(entry.file), entry.file, schemaId);
        }
      }
    },
    [classifyPendingEntry, pendingEntries, schemaNameById]
  );

  const handlePendingEntryReportTypeChange = useCallback((index: number, reportType: string) => {
    setPendingEntries((current) =>
      current.map((item, entryIndex) => (entryIndex === index ? { ...item, reportType } : item))
    );
  }, []);

  const handlePendingEntryRemove = useCallback((index: number) => {
    setPendingEntries((current) => current.filter((_, entryIndex) => entryIndex !== index));
  }, []);

  const canCreateBatch = canSubmitPendingPipelineUploads(
    pendingEntries,
    schemaNameById,
    schemaRequiresReportType
  );

  const handleBeginUpload = () => {
    if (!providerReady) return;
    if (pendingEntries.length === 0) return;
    setAssignOpen(true);
  };

  const handleCreateBatch = () => {
    if (!canCreateBatch) return;
    setPipelineUploadHandoff({
      entries: pendingEntries,
      providerId: providerRequired ? providerId : undefined,
    });
    track("home_create_batch", {
      fileCount: pendingEntries.length,
      providerId: providerId ?? "none",
    });
    setAssignOpen(false);
    navigate("/pipeline");
  };

  return (
    <div className="mx-auto w-full max-w-[1180px] px-5 py-10 md:px-6 md:py-11">
      <div className="mb-8 max-w-xl md:mb-9">
        <h1 className="mb-2.5 text-[2rem] font-extrabold leading-tight tracking-tight text-[var(--color-text-primary)] md:text-[2.375rem]">
          Which files would you like to process today?
        </h1>
        <p className="text-[15px] leading-relaxed text-[var(--color-text-secondary)]">
          Upload one file or many at once — we&apos;ll help you classify each one, map it to a
          schema, and walk it through validation.
        </p>
      </div>

      <section className="max-w-3xl rounded-xl border border-[var(--color-border-light)] bg-[var(--color-bg-primary)] p-6 shadow-sm">
        {providerRequired ? (
          <div className="mb-5">
            <label className="mb-2 block text-sm font-semibold text-[var(--color-text-primary)]">
              Provider
            </label>
            <ProviderSelector
              value={providerId}
              onChange={handleProviderChange}
              allowedStatus="active"
              emptyMessage="None of your assigned providers are active yet. Contact an admin."
            />
          </div>
        ) : null}

        <h2 className="mb-1 text-[15px] font-bold text-[var(--color-text-primary)]">
          Start a new upload
        </h2>
        <p className="mb-4 text-[12.5px] text-[var(--color-text-tertiary)]">
          Drop in up to {HOME_UPLOAD_MAX_FILES} files — you&apos;ll assign a schema to each before
          we begin processing.
        </p>

        {(uploadError || schemasError) && (
          <div className="mb-4">
            <AlertMessage
              variant="error"
              message={uploadError || schemasError}
              onDismiss={() => {
                setUploadError("");
                setSchemasError("");
              }}
            />
          </div>
        )}

        <MultiFileUploadField
          files={pendingEntries.map((entry) => entry.file)}
          onFilesChange={handlePendingFilesAdd}
          onError={setUploadError}
          maxFiles={HOME_UPLOAD_MAX_FILES}
          showFileList={false}
          disabled={!providerReady}
          className="max-w-none w-full"
        />

        {pendingEntries.length > 0 ? (
          <ul className="mt-4 flex flex-col gap-2">
            {pendingEntries.map((entry, index) => (
              <li
                key={pendingFileKey(entry.file)}
                className="flex items-center gap-2.5 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] px-3 py-2"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]">
                  <FileText className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden />
                </span>
                <span
                  className="min-w-0 flex-1 truncate font-mono text-xs font-semibold text-[var(--color-text-primary)]"
                  title={entry.file.name}
                >
                  {entry.file.name}
                </span>
                <span className="shrink-0 font-mono text-[11px] text-[var(--color-text-tertiary)]">
                  {(entry.file.size / 1024).toFixed(0)} KB
                </span>
                <button
                  type="button"
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:bg-red-50 hover:text-red-800"
                  aria-label={`Remove ${entry.file.name}`}
                  onClick={() => handlePendingEntryRemove(index)}
                >
                  <X className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="text-[12.5px] text-[var(--color-text-tertiary)]">
            {pendingEntries.length === 0
              ? "No files added yet"
              : `${pendingEntries.length} file${pendingEntries.length === 1 ? "" : "s"} added`}
          </span>
          <Button
            type="button"
            className="gap-2"
            disabled={!providerReady || pendingEntries.length === 0}
            onClick={handleBeginUpload}
            data-testid="home-begin-upload"
          >
            <Upload className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden />
            Begin upload
          </Button>
        </div>
      </section>

      <AnimatedDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        maxWidth="max-w-2xl"
        className="max-h-[86vh] overflow-hidden p-0"
      >
        <div className="flex max-h-[86vh] flex-col">
          <div className="flex items-start justify-between border-b border-[var(--color-border-light)] px-5 py-4">
            <div>
              <Dialog.Title className="text-base font-extrabold text-[var(--color-text-primary)]">
                Assign schemas
              </Dialog.Title>
              <Dialog.Description className="mt-0.5 text-[12.5px] text-[var(--color-text-tertiary)]">
                Confirm what each file is before we start processing
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
                aria-label="Close"
              >
                <X className="h-4 w-4" strokeWidth={1.8} aria-hidden />
              </button>
            </Dialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3.5">
            {providerRequired && providerId ? (
              <div className="mb-3.5" data-testid="assign-schemas-provider-reminder">
                <label className="mb-2 block text-sm font-semibold text-[var(--color-text-primary)]">
                  Provider
                </label>
                <LockedProviderField label={providerName ?? providerId} />
              </div>
            ) : null}
            <PendingPipelineFilesList
              entries={pendingEntries}
              schemas={schemas}
              isLoadingSchemas={isLoadingSchemas}
              onEntrySchemaChange={handlePendingEntrySchemaChange}
              onAllSchemasChange={handlePendingAllSchemasChange}
              onEntryReportTypeChange={handlePendingEntryReportTypeChange}
              onRemoveEntry={handlePendingEntryRemove}
            />
          </div>

          <div className="flex items-center justify-end gap-2.5 border-t border-[var(--color-border-light)] px-5 py-4">
            <Button type="button" variant="secondary" onClick={() => setAssignOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!canCreateBatch || isLoadingSchemas}
              onClick={handleCreateBatch}
              data-testid="home-create-batch"
            >
              Confirm Upload
            </Button>
          </div>
        </div>
      </AnimatedDialog>
    </div>
  );
}
